import { Check, ArrowUpRight } from "lucide-react";
import Seo from "@/components/site/Seo";
import Marquee from "@/components/site/Marquee";
import QuoteButton from "@/components/site/QuoteButton";
import { Reveal, MaskReveal } from "@/components/site/Reveal";
import { DOORS, COMPOSITE_GALLERY, UPVC_GALLERY } from "@/lib/site";

const SWATCHES = {
  "Duck Egg Blue": "#7FA6A0",
  "Anthracite Grey": "#383E42",
  "Chartwell Green": "#5C6E5B",
  "Agate Grey": "#6E6F6B",
};

export default function Doors() {
  return (
    <>
      <Seo
        title="Doors | Composite, uPVC & French Doors | Eminence Windows & Doors"
        description="Explore our door range — premium composite doors, modern uPVC doors and elegant French doors. Stylish, secure and built to last across Greater Manchester."
      />

      {/* Hero */}
      <section className="relative flex min-h-[70vh] items-end overflow-hidden bg-charcoal" data-testid="doors-hero">
        <img
          src={DOORS[0].image}
          alt="Elegant composite front door"
          className="absolute inset-0 h-full w-full object-cover opacity-50"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/60 to-charcoal/20" />
        <div className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-16 pt-40 lg:px-10">
          <p className="kicker text-gold">Our door range</p>
          <h1 className="mt-6 font-serif text-5xl leading-[1] text-cream md:text-7xl">
            <MaskReveal lines={["Stylish, secure", "and built to last."]} />
          </h1>
          <p className="mt-6 max-w-xl text-cream/80">
            A wide choice of designs, colours and glass styles to perfectly suit
            your home.
          </p>
        </div>
      </section>

      <Marquee
        items={["Composite Doors", "uPVC Doors", "French Doors", "CERTASS Certified Installers", "Secure by Design", "10 Year Insurance-Backed Guarantee"]}
      />

      {/* Products */}
      <div className="mx-auto max-w-7xl px-5 lg:px-10">
        {DOORS.map((d, i) => (
          <div key={d.slug}>
          <section
            className="grid items-center gap-10 border-b border-gold/15 py-20 lg:grid-cols-2 lg:gap-16 md:py-28"
            data-testid={`door-${d.slug}`}
          >
            <Reveal className={i % 2 === 1 ? "lg:order-2" : ""}>
              <div className="overflow-hidden rounded-3xl">
                <img
                  src={d.image}
                  alt={d.name}
                  className="aspect-[4/5] w-full object-cover transition-transform duration-[900ms] hover:scale-105"
                />
              </div>
            </Reveal>

            <Reveal delay={0.1} className={i % 2 === 1 ? "lg:order-1" : ""}>
              <p className="kicker text-gold-deep">{d.tagline}</p>
              <h2 className="mt-4 font-serif text-4xl text-ink md:text-5xl">
                {d.name}
              </h2>
              <p className="mt-5 leading-relaxed text-stone">{d.overview}</p>

              <ul className="mt-7 grid gap-3 sm:grid-cols-2">
                {d.benefits.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-sm text-ink">
                    <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-gold-deep" />
                    {b}
                  </li>
                ))}
              </ul>

              {d.colours.length > 0 && (
                <div className="mt-8">
                  <p className="kicker text-stone">Popular finishes</p>
                  <div className="mt-4 flex flex-wrap gap-4">
                    {d.colours.map((c) => (
                      <div key={c.name} className="flex items-center gap-2.5">
                        <span
                          className="h-7 w-7 rounded-full border border-black/10 shadow-inner"
                          style={{ background: SWATCHES[c.name] || "#999" }}
                        />
                        <div className="leading-tight">
                          <p className="text-sm font-medium text-ink">{c.name}</p>
                          <p className="text-xs text-stone">{c.glass}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-9">
                <QuoteButton
                  label={`Enquire about ${d.name}`}
                  testId={`door-${d.slug}-enquire-btn`}
                />
              </div>
            </Reveal>
          </section>

          {d.slug === "composite" && (
            <section
              className="border-b border-gold/15 py-16 md:py-24"
              data-testid="composite-gallery"
            >
              <Reveal className="max-w-2xl">
                <p className="kicker text-gold-deep">The composite door range</p>
                <h3 className="mt-3 font-serif text-3xl text-ink md:text-4xl">
                  A style &amp; finish for every home
                </h3>
                <p className="mt-4 leading-relaxed text-stone">
                  From classic to contemporary — explore a selection of our most
                  popular composite door designs, each available in a wide choice
                  of colours and glass.
                </p>
              </Reveal>
              <div className="mt-10 grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-5">
                {COMPOSITE_GALLERY.map((g, gi) => (
                  <Reveal key={`${g.name}-${g.finish}`} delay={gi * 0.06}>
                    <figure
                      className="group overflow-hidden rounded-2xl border border-gold/15 bg-cream-card"
                      data-testid={`composite-door-${gi}`}
                    >
                      <div className="aspect-[3/4] overflow-hidden">
                        <img
                          src={g.image}
                          alt={`${g.name} composite door in ${g.finish}`}
                          loading="lazy"
                          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                      </div>
                      <figcaption className="px-4 py-4">
                        <p className="font-serif text-lg leading-tight text-ink">
                          {g.name}
                        </p>
                        <p className="mt-1 text-xs text-stone">{g.finish}</p>
                      </figcaption>
                    </figure>
                  </Reveal>
                ))}
              </div>
            </section>
          )}

          {d.slug === "upvc" && (
            <section
              className="border-b border-gold/15 py-16 md:py-24"
              data-testid="upvc-gallery"
            >
              <Reveal className="max-w-2xl">
                <p className="kicker text-gold-deep">The uPVC door range</p>
                <h3 className="mt-3 font-serif text-3xl text-ink md:text-4xl">
                  Classic panelled designs
                </h3>
                <p className="mt-4 leading-relaxed text-stone">
                  Timeless, low-maintenance uPVC doors available in a variety of
                  panel and glazing styles to suit your home.
                </p>
              </Reveal>
              <div className="mt-10 grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-5">
                {UPVC_GALLERY.map((g, gi) => (
                  <Reveal key={g.name} delay={gi * 0.06}>
                    <figure
                      className="group overflow-hidden rounded-2xl border border-gold/15 bg-cream-deep"
                      data-testid={`upvc-door-${gi}`}
                    >
                      <div className="flex aspect-[3/4] items-center justify-center overflow-hidden bg-cream-deep p-2">
                        <img
                          src={g.image}
                          alt={`${g.name} uPVC door in ${g.finish}`}
                          loading="lazy"
                          className="max-h-full max-w-full object-contain transition-transform duration-700 group-hover:scale-105"
                        />
                      </div>
                      <figcaption className="px-4 py-4 text-center">
                        <p className="font-serif text-lg leading-tight text-ink">
                          {g.name}
                        </p>
                        <p className="mt-1 text-xs text-stone">{g.finish}</p>
                      </figcaption>
                    </figure>
                  </Reveal>
                ))}
              </div>
            </section>
          )}
          </div>
        ))}
      </div>

      {/* CTA */}
      <section className="bg-cream-deep py-24 md:py-28" data-testid="doors-cta">
        <div className="mx-auto flex max-w-4xl flex-col items-center px-5 text-center lg:px-10">
          <Reveal>
            <h2 className="font-serif text-4xl text-ink md:text-5xl">
              Not sure which door is right for you?
            </h2>
            <p className="mx-auto mt-5 max-w-xl leading-relaxed text-stone">
              Our experts offer honest advice to help you find the perfect door
              for your home — with a free, no-obligation quote.
            </p>
            <div className="mt-9 flex justify-center">
              <QuoteButton size="lg" testId="doors-request-quote-btn" />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
